import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip } from "@/components/chart";
import ChartTemplate from "@/components/chart-template";
import { ConversionTimingHistogramRow } from "@/http/api-types";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  bucketLabels,
  bucketOrder,
  CustomTooltipContent,
  formatDuration,
  primaryHistogramConfig,
  secondaryHistogramConfig,
} from "./chart-utils";

type ChartData = {
  bucket: string;
  bucketLabel: string;
  bucketOrder: number;
  percentage: number;
  count: number;
};

type ConversionTimingHistogramChartProps = {
  data: ConversionTimingHistogramRow[];
  type: "primary" | "secondary";
  medianSeconds: number;
  averageSeconds: number;
};

function processChartData(data: ConversionTimingHistogramRow[], type: "primary" | "secondary"): ChartData[] {
  return data
    .filter(row => row.time_bucket !== "negative" && row.time_bucket !== "no_timestamp")
    .map(row => ({
      bucket: row.time_bucket,
      bucketLabel: bucketLabels[row.time_bucket] || row.time_bucket,
      bucketOrder: bucketOrder[row.time_bucket] || 999,
      percentage: type === "primary" ? row.ap_percentage : row.secondary_ap_percentage,
      count: type === "primary" ? row.ap_conversions_count : row.secondary_ap_conversions_count,
    }))
    .sort((a, b) => a.bucketOrder - b.bucketOrder);
}

function ConversionTimingHistogramChart({
  data,
  type,
  medianSeconds,
  averageSeconds,
}: ConversionTimingHistogramChartProps) {
  const chartData = processChartData(data, type);
  const config = type === "primary" ? primaryHistogramConfig : secondaryHistogramConfig;

  const chartContent = (
    <>
      <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
        <div className="flex flex-col">
          <span className="text-muted-foreground">Median Time to Conversion</span>
          <span className="font-medium">{formatDuration(medianSeconds)}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-muted-foreground">Average Time to Conversion</span>
          <span className="font-medium">{formatDuration(averageSeconds)}</span>
        </div>
      </div>

      <ChartContainer config={config}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucketLabel" tickLine={false} axisLine={false} tickMargin={8} />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            domain={[0, 100]}
            tickFormatter={(value: number) => `${value}%`}
          />
          <ChartTooltip cursor={{ fill: "rgba(0, 0, 0, 0.1)" }} content={<CustomTooltipContent config={config} />} />

          <Bar dataKey="percentage" fill={`var(--color-percentage)`} radius={[4, 4, 0, 0]} fillOpacity={0.8} />

          <ChartLegend content={<ChartLegendContent />} />
        </BarChart>
      </ChartContainer>
    </>
  );

  return (
    <ChartTemplate
      chart={chartContent}
      table={null}
      title={`Bid to ${type === "primary" ? "AP" : "2nd AP"} Conversion Time Distribution`}
      description={`Time from bid to ${type === "primary" ? "AP" : "2nd AP"} conversion`}
      summaryMetric={formatDuration(medianSeconds)}
      summaryMetricLabel="Median Time"
      timeSplittable={false}
    />
  );
}

export default ConversionTimingHistogramChart;
